import {Button} from "antd";
import {useEffect, useState} from "react";
import OTPInput from "react-otp-input";
import styled from "styled-components";
import {
  StyledOtpInput,
  StyledTextButton,
} from "../../../payout-settings-tab/otp-view/styles";
import {remainingResendSeconds} from "./resend-countdown";

const StyledWithdrawal2FAOtpInput = styled(StyledOtpInput)`
  && {
    flex: 1 1 0;
    width: 100% !important;
    min-width: 0;
    height: 6.4rem !important;
  }
`;

type TWithdrawal2FAView = "code" | "recovery";

interface Withdrawal2FAStepProps {
  method: "email" | "totp";
  isSubmitting: boolean;
  isResending: boolean;
  /** Epoch ms the resend cooldown expires; 0 when no code has been sent. */
  resendAvailableAt: number;
  onResend: () => void;
  onBack: () => void;
  onSubmit: (twofaCode: string) => Promise<void>;
}

/**
 * Second step of the withdrawal modal. The code entered here is sent with the
 * withdrawal itself — POST payments/withdrawal verifies it inline, so there is
 * no separate verify call and no withdrawal until a valid code is supplied.
 */
export function Withdrawal2FAStep({
  method,
  isSubmitting,
  isResending,
  resendAvailableAt,
  onResend,
  onBack,
  onSubmit,
}: Withdrawal2FAStepProps) {
  const [view, setView] = useState<TWithdrawal2FAView>("code");
  const [otp, setOtp] = useState("");
  const [timer, setTimer] = useState(() =>
    remainingResendSeconds(resendAvailableAt, Date.now())
  );

  // Derived from the deadline rather than counted down locally, so stepping
  // back to the amount view and returning resumes the real cooldown.
  useEffect(() => {
    if (method !== "email") {
      return;
    }

    setTimer(remainingResendSeconds(resendAvailableAt, Date.now()));

    const timerCount = setInterval(() => {
      setTimer(remainingResendSeconds(resendAvailableAt, Date.now()));
    }, 1000);

    return () => clearInterval(timerCount);
  }, [method, resendAvailableAt]);

  const handleResend = () => {
    setOtp("");
    onResend();
  };

  const handleShowRecovery = () => {
    setView("recovery");
    setOtp("");
  };

  const handleBackToCode = () => {
    setView("code");
    setOtp("");
  };

  // A rejected withdrawal keeps the coach on this step so they can retry; the
  // mutation's own onError surfaces the reason from the API.
  const handleSubmit = async () => {
    try {
      await onSubmit(otp);
    } catch {
      setOtp("");
    }
  };

  const otpInputRow = (
    <OTPInput
      value={otp}
      onChange={setOtp}
      numInputs={6}
      shouldAutoFocus
      inputType="tel"
      containerStyle={{display: "flex", gap: "12px", width: "100%"}}
      renderInput={(props) => <StyledWithdrawal2FAOtpInput {...props} />}
    />
  );

  const submitButton = (
    <Button
      size="small"
      style={{flex: 1}}
      type="primary"
      disabled={otp.length < 6}
      loading={isSubmitting}
      onClick={handleSubmit}
    >
      Withdraw earnings
    </Button>
  );

  if (view === "recovery") {
    return (
      <div style={{display: "flex", flexDirection: "column", gap: "24px"}}>
        <div style={{display: "flex", flexDirection: "column", gap: "24px"}}>
          <h2 style={{fontSize: "2rem", fontWeight: 600, margin: 0}}>
            Enter a recovery code
          </h2>
          <p style={{margin: 0}}>
            Enter one of the recovery codes you saved. Each code works once
          </p>
          {otpInputRow}
        </div>

        <div style={{display: "flex", gap: "16px", width: "100%"}}>
          <Button size="small" style={{flex: 1}} onClick={handleBackToCode}>
            Back
          </Button>
          {submitButton}
        </div>
      </div>
    );
  }

  return (
    <div style={{display: "flex", flexDirection: "column", gap: "24px"}}>
      <div style={{display: "flex", flexDirection: "column", gap: "24px"}}>
        <h2 style={{fontSize: "2rem", fontWeight: 600, margin: 0}}>
          {method === "email" ? "Check your email" : "Enter the 6-digit code"}
        </h2>
        <p style={{margin: 0}}>
          {method === "email"
            ? "Enter the 6-digit code sent to your email to confirm this withdrawal"
            : "Enter the code from your authenticator app to confirm this withdrawal"}
        </p>
        {otpInputRow}
      </div>

      {method === "email" && (
        <p style={{margin: 0}}>
          Didn&apos;t get code?{" "}
          {timer > 0 ? (
            <span style={{color: "rgba(0,0,0,0.45)"}}>Resend in {timer}s</span>
          ) : (
            <StyledTextButton
              type="text"
              size="small"
              loading={isResending}
              onClick={handleResend}
            >
              <strong style={{fontWeight: 600}}>Resend code</strong>
            </StyledTextButton>
          )}
        </p>
      )}

      <div style={{display: "flex", gap: "16px", width: "100%"}}>
        <Button size="small" style={{flex: 1}} onClick={onBack}>
          Back
        </Button>
        {submitButton}
      </div>

      {method === "totp" && (
        <StyledTextButton
          type="text"
          size="small"
          style={{alignSelf: "flex-start"}}
          onClick={handleShowRecovery}
        >
          <span className="text-underline">
            Can&apos;t access your authenticator app?
          </span>
        </StyledTextButton>
      )}
    </div>
  );
}
